// PremiumRoute — route-level gate for Pro/Business-only surfaces (Ad Strategist,
// Ad Pilot). Jul 24 tiering.
//
//   - Pro / Business  → render the real page verbatim.
//   - Free / Starter  → render a FEATURE LANDING PAGE: hero + "what it does"
//                       points + an Upgrade CTA. The wrapped page NEVER mounts
//                       for them, so its data effects / API calls don't fire.
//                       (Rules-of-hooks safe: the guard is its own component, so
//                       the child's hooks only run when it's actually rendered.)
//
// Note: the shared competitor/AS backend endpoints stay OPEN because the brand
// onboarding wizard reuses them under the hood. This client gate is the UX/entry
// control for the STANDALONE feature pages — lower tiers can explore what the
// feature offers, then upgrade.

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from './AppLayoutV2';
import { useCredits } from '@/hooks/useCredits';

export interface PremiumFeaturePoint {
  icon: string; // Remix Icon class
  title: string;
  body: string;
}

interface PremiumRouteProps {
  /** Human-readable feature name (e.g. "Ad Strategist"). */
  feature: string;
  /** Hero icon (Remix Icon class). */
  icon: string;
  /** One-line pitch under the title. */
  tagline: string;
  /** 3–4 capability points shown as cards. */
  points: PremiumFeaturePoint[];
  children: ReactNode;
}

export default function PremiumRoute({ feature, icon, tagline, points, children }: PremiumRouteProps) {
  const { planTier, loading } = useCredits();
  const navigate = useNavigate();

  // Don't flash the landing page before the plan resolves.
  if (loading) {
    return (
      <AppLayoutV2>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
        </div>
      </AppLayoutV2>
    );
  }

  if (planTier === 'pro' || planTier === 'business') {
    return <>{children}</>;
  }

  // Free / Starter → feature landing page (the real page below never mounts).
  return (
    <AppLayoutV2>
      <div className="w-full max-w-[900px] mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-light text-primary text-[11px] font-bold uppercase tracking-wider">
            <i className="ri-vip-crown-2-line" />
            Pro feature
          </span>
          <div className="mt-5 inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary-hover text-white items-center justify-center shadow-lg shadow-primary/25">
            <i className={`${icon} text-3xl`} />
          </div>
          <h1 className="mt-5 text-3xl font-bold text-dark">{feature}</h1>
          <p className="mt-3 text-dark-muted text-lg leading-relaxed max-w-[560px] mx-auto">{tagline}</p>
          <button
            type="button"
            onClick={() => navigate('/checkout')}
            className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-up-circle-line text-base" />
            Upgrade to Pro
          </button>
          <p className="mt-2 text-xs text-dark-muted">Available on Pro &amp; Business plans</p>
        </div>

        {/* What it does */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {points.map((p, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-surface-dark/30 shadow-sm"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-light text-primary flex items-center justify-center">
                <i className={`${p.icon} text-xl`} />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-dark text-sm">{p.title}</p>
                <p className="mt-1 text-sm text-dark-muted leading-relaxed">{p.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <button
            type="button"
            onClick={() => navigate('/checkout')}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-up-circle-line text-base" />
            Upgrade to unlock {feature}
          </button>
        </div>
      </div>
    </AppLayoutV2>
  );
}
